import {
  CssColor,
  CssFont,
  type CssColorValue,
  type CssFontValue,
  DataType,
  type SettingTypeMetadata,
  StyleType,
  type StyleTypeMetadata,
  wixColor,
  wixFont,
} from '@wix/editor-platform-ooi-metadata-resolver'
import type {ISiteTextPreset} from '@wix/yoshi-flow-editor'

import {Alignment, ImageSize} from '../types/createStylesParams'

const THEME_COLOR_BY_PRESET = {
  'color-1': CssColor.WstPrimaryBackgroundColor,
  'color-5': CssColor.WstParagraph2Color,
  'color-8': CssColor.WstLinksAndActionsColor,
  'color-37': CssColor.WstParagraph2Color,
  'color-text-primary': CssColor.WstParagraph2Color,
  'color-action': CssColor.WstLinksAndActionsColor,
  'color-title': CssColor.WstHeading2Color,
  'color-line': CssColor.WstParagraph2Color,
} as const satisfies Record<string, CssColorValue>

const THEME_FONT_BY_PRESET = {
  'Page-title': CssFont.WstHeading2Font,
  'Heading-M': CssFont.WstHeading5Font,
  'Body-M': CssFont.WstParagraph2Font,
} as const satisfies Partial<Record<keyof ISiteTextPreset, CssFontValue>>

export type ThemeColorPreset = keyof typeof THEME_COLOR_BY_PRESET
export type ThemeFontPreset = keyof typeof THEME_FONT_BY_PRESET

export type ColorStyleType = StyleType.color | StyleType.backgroundColor | StyleType.borderColor

export type NumericStyleType =
  | StyleType.number
  | StyleType.gap
  | StyleType.padding
  | StyleType.borderWidth
  | StyleType.borderTopLeftRadius
  | StyleType.borderTopRightRadius
  | StyleType.borderBottomLeftRadius
  | StyleType.borderBottomRightRadius

type DefaultValues<T> = {
  defaultValue: T
  mobileDefaultValue?: T
}

const staticBranch = <T>(value: T) => ({type: 'static' as const, value})

const defaultBranches = <T>({defaultValue, mobileDefaultValue}: DefaultValues<T>) => ({
  defaultValue: staticBranch(defaultValue),
  ...(mobileDefaultValue !== undefined && {mobileDefaultValue: staticBranch(mobileDefaultValue)}),
})

const aiDescription = (description?: string) => (description ? {ai: {description}} : {})

export const booleanMetadata = (
  defaultValue: boolean,
  description: string,
): SettingTypeMetadata => ({
  type: DataType.booleanValue,
  defaultValue: staticBranch(defaultValue),
  ...aiDescription(description),
})

export const textMetadata =
  (translationKey: string, description: string): SettingTypeMetadata =>
  ({t}) => ({
    type: DataType.text,
    defaultValue: staticBranch(t(translationKey)),
    ...aiDescription(description),
  })

export const colorMetadata = (
  styleType: ColorStyleType,
  preset: ThemeColorPreset,
  opacity?: number,
): StyleTypeMetadata => ({
  type: styleType,
  defaultValue: wixColor(THEME_COLOR_BY_PRESET[preset], opacity),
})

export const fontMetadata = (preset: ThemeFontPreset, size?: number): StyleTypeMetadata => ({
  type: StyleType.font,
  defaultValue: {
    ...wixFont(THEME_FONT_BY_PRESET[preset]),
    ...(size !== undefined && {size}),
  },
})

export const numberMetadata = (
  styleType: NumericStyleType,
  defaults: DefaultValues<number>,
  description?: string,
): StyleTypeMetadata => ({
  type: styleType as StyleType.number,
  ...defaultBranches(defaults),
  ...aiDescription(description),
})

export const numberEnumMetadata = (
  defaults: DefaultValues<number>,
  description: string,
): StyleTypeMetadata => ({
  type: StyleType.numberEnum,
  ...defaultBranches(defaults),
  ...aiDescription(description),
})

export const ALIGNMENT_DESCRIPTION = 'Alignment: 1 = left, 2 = center, 3 = right'
export const ALIGNMENT_WITH_STRETCH_DESCRIPTION =
  'Alignment: 1 = left, 2 = center, 3 = right, 4 = stretch'
export const IMAGE_SIZE_DESCRIPTION = 'Image resize: 1 = crop, 2 = fit'
export const TOGGLE_DESCRIPTION = '1 = on, 0 = off'

type NumberEnumOption = {value: number; displayName: string}

export const ALIGNMENT_OPTIONS: NumberEnumOption[] = [
  {value: Alignment.Left, displayName: 'Left'},
  {value: Alignment.Center, displayName: 'Center'},
  {value: Alignment.Right, displayName: 'Right'},
]
export const ALIGNMENT_WITH_STRETCH_OPTIONS: NumberEnumOption[] = [
  ...ALIGNMENT_OPTIONS,
  {value: Alignment.Stretch, displayName: 'Stretch'},
]
export const IMAGE_SIZE_OPTIONS: NumberEnumOption[] = [
  {value: ImageSize.Crop, displayName: 'Crop'},
  {value: ImageSize.Fit, displayName: 'Fit'},
]
export const TOGGLE_OPTIONS: NumberEnumOption[] = [
  {value: 0, displayName: 'Off'},
  {value: 1, displayName: 'On'},
]
